import {Subject} from "rxjs";
import {useEffect, useState} from "react";
import {logger} from "../log/logger";

export type Unsubscribe = () => void;
export type Subscribe<TMessage> = (callback: (message: TMessage) => void) => Unsubscribe;
export type EmitMessage<TMessage> = (m: TMessage) => void;

export function protectedSubscribe<TMessage>(subject: Subject<TMessage>, callback: (message: TMessage) => void): Unsubscribe {
  const protectedCallback = (message: TMessage) => {
    try{
      callback(message);
    }catch(e){
      const error = e as Error;
      logger.error({ message: `error encountered in observer of ${message}`, error: error});    }
  };
  const subscription = subject.subscribe(protectedCallback);
  const unsubscribe = subscription.unsubscribe.bind(subscription);
  return unsubscribe;
}

export function createSubjectForMessage<TMessage>(): Subject<TMessage>{
  return new Subject<TMessage>();
}

export function createEmitMessageFunction<TMessage>(subject: Subject<TMessage>): EmitMessage<TMessage>{
  return (m: TMessage) => subject.next(m);
}

export function createEventBus<TMessage>(){
  const eventBus = createSubjectForMessage<TMessage>();
  const emitMessage = createEmitMessageFunction<TMessage>(eventBus);
  const subscribe: Subscribe<TMessage> = (callback: (message: TMessage) => void) => protectedSubscribe(eventBus, callback);
  return { subscribe, eventBus, emitMessage,};
}

export function hookifyEventSubscription<TMessage>(subscribe: Subscribe<TMessage>, initialValue: TMessage){
  return () => {
    const [value, setValue] = useState<TMessage>(initialValue);
    const unsubscribe = subscribe(setValue);//use effect can take several seconds so subscribe to changes until it runs.
    useEffect(()=> {
      unsubscribe();
      return subscribe(setValue);
    }, [unsubscribe]);
    return value;
  }
}

export function createEventBusAndHook<TMessage>(initialValue: TMessage){
  const {subscribe, emitMessage} = createEventBus<TMessage>();
  const use = hookifyEventSubscription<TMessage>(subscribe, initialValue);
  return {subscribe, emitMessage, use: use,};
}






